import styles from './SeatsChartChangePreviewPanel.module.css';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getSeatsChartChangePreview, SeatsChartChangePreview } from 'services/Seats';
import { chartChangeCsvQueryKey } from './csv';

interface Props {
	eventId: string;
	newChartKey: string;
	onLoadingChange?: (loading: boolean) => void;
	onErrorChange?: (error: boolean) => void;
}

export const SeatsChartChangePreviewPanel = ({ eventId, newChartKey, onLoadingChange, onErrorChange }: Props) => {
	const { t } = useTranslation();

	const { data, isLoading, isError, error } = useQuery<SeatsChartChangePreview>({
		queryKey: chartChangeCsvQueryKey(eventId, newChartKey),
		queryFn: () => getSeatsChartChangePreview(eventId, newChartKey),
		enabled: !!eventId && !!newChartKey,
		// snapshot for the session — stale after 30s so a different chart pick refetches
		staleTime: 30_000,
		onSuccess: () => onErrorChange?.(false),
		onError: () => onErrorChange?.(true),
	});

	// Mirror loading state back to the parent (it disables Save while the preview computes).
	// Pushed via an effect, not in render, to avoid updating the parent during this child's render.
	useEffect(() => {
		onLoadingChange?.(isLoading);
	}, [isLoading, onLoadingChange]);

	// Clear both flags on unmount so a parent Save-gate can't latch disabled after the panel goes away
	// (the manager locks the chart, deselects it, or saves — or it unmounts mid-fetch). Without this, an
	// errored/loading preview left the parent's chartPreviewError/Loading true until a full reload.
	useEffect(
		() => () => {
			onLoadingChange?.(false);
			onErrorChange?.(false);
		},
		[onLoadingChange, onErrorChange]
	);

	if (isLoading) {
		return (
			<details className={styles.panelDetails} open>
				<summary className={styles.panelSummary}>
					{t('seats-preview-calculating', 'Calculating chart change impact…')}
				</summary>
			</details>
		);
	}

	if (isError || !data) {
		return (
			<details className={styles.panelDetails} open>
				<summary className={styles.panelSummary}>
					{t('seats-preview-failed', 'Could not calculate chart change impact')}
				</summary>
				<section className={styles.panelSection}>
					<p>
						{error instanceof Error ? error.message : t('seats-preview-retry-hint', 'Try selecting the chart again.')}
					</p>
				</section>
			</details>
		);
	}

	const { seats } = data;
	const categories = {
		kept: data.categories?.kept ?? [],
		added: data.categories?.added ?? [],
		removed: data.categories?.removed ?? [],
	};
	const orphaned_seats = data.orphaned_seats ?? [];
	const warnings = data.warnings ?? [];
	const totalChanges =
		categories.added.length + categories.removed.length + seats.will_change_category + seats.will_orphan;

	return (
		<details className={styles.panelDetails} open={totalChanges > 0}>
			<summary className={styles.panelSummary}>
				{t('seats-preview-title', 'Chart change preview')} —{' '}
				<span className={styles.pill}>{t('seats-preview-count-new', '{{n}} new', { n: categories.added.length })}</span>{' '}
				<span className={styles.pill}>
					{t('seats-preview-count-removed', '{{n}} removed', { n: categories.removed.length })}
				</span>{' '}
				<span className={styles.pill}>
					{t('seats-preview-count-kept', '{{n}} kept', { n: categories.kept.length })}
				</span>{' '}
				<span className={styles.pill} data-tone="warn">
					{t('seats-preview-count-orphaned', '{{n}} orphaned seats', { n: seats.will_orphan })}
				</span>
			</summary>

			<section className={styles.panelSection}>
				<h4>{t('seats-preview-seats', 'Seats')}</h4>
				<div className={styles.row}>
					<div className={styles.stat}>
						<b>{seats.currently_booked}</b>
						<span>{t('seats-preview-currently-booked', 'currently booked')}</span>
					</div>
					<div className={styles.stat}>
						<b>{seats.will_rebook}</b>
						<span>{t('seats-preview-will-rebook', 'will rebook on new chart')}</span>
					</div>
					<div className={styles.stat}>
						<b>{seats.will_change_category}</b>
						<span>{t('seats-preview-move-category', 'move to different category')}</span>
					</div>
					<div className={styles.stat} data-tone="warn">
						<b>{seats.will_orphan}</b>
						<span>{t('seats-preview-no-match', 'no match — kept as info')}</span>
					</div>
				</div>
			</section>

			{categories.added.length > 0 && (
				<section className={styles.panelSection}>
					<h4>{t('seats-preview-new-categories', 'New categories')}</h4>
					<ul>
						{categories.added.map((c) => (
							<li key={c.key}>
								<b>{c.label}</b> — {t('seats-preview-n-seats', '{{n}} seats', { n: c.new_capacity })}
							</li>
						))}
					</ul>
				</section>
			)}

			{categories.kept.length > 0 && (
				<section className={styles.panelSection}>
					<h4>{t('seats-preview-existing-categories', 'Existing categories (capacity will be adjusted)')}</h4>
					<ul>
						{categories.kept.map((c) => (
							<li key={c.ticket_type_id}>
								<b>{c.label}</b>: {c.old_capacity} → {c.new_capacity} {t('seats-preview-seats-lower', 'seats')}
								{c.sold > 0 && ` (${t('seats-preview-n-sold', '{{n}} sold', { n: c.sold })})`}
								{c.old_key && c.old_key !== c.key && (
									<> — {t('seats-preview-rekeyed', 're-keyed {{from}} → {{to}}', { from: c.old_key, to: c.key })}</>
								)}
							</li>
						))}
					</ul>
				</section>
			)}

			{categories.removed.length > 0 && (
				<section className={styles.panelSection}>
					<h4>{t('seats-preview-removed-categories', 'Removed categories')}</h4>
					<ul>
						{categories.removed.map((c) => (
							<li key={c.key}>
								<b>{c.label}</b>
								{c.action === 'disable_ticket_type'
									? ` — ${t('seats-preview-disable-type', '{{n}} sold — ticket type will be disabled', { n: c.sold })}`
									: ` — ${t('seats-preview-unlink-type', 'ticket type will be unlinked from chart')}`}
							</li>
						))}
					</ul>
				</section>
			)}

			{orphaned_seats.length > 0 && (
				<section className={styles.panelSection}>
					<h4>{t('seats-preview-orphaned-seats', 'Orphaned seats')}</h4>
					<p>
						{t(
							'seats-preview-orphaned-help',
							'These seats do not exist in the new chart and will not be rebooked. You can reassign them manually after saving.'
						)}
					</p>
					<ul>
						{orphaned_seats.slice(0, 25).map((o) => (
							<li key={o.ticket_id}>
								{t('seats-preview-seat', 'Seat')} <b>{o.seat_label}</b> —{' '}
								{t('seats-preview-ticket-id', 'Ticket #{{id}}', { id: o.ticket_id })}
							</li>
						))}
						{orphaned_seats.length > 25 && (
							<li>{t('seats-preview-and-more', '… and {{n}} more', { n: orphaned_seats.length - 25 })}</li>
						)}
					</ul>
				</section>
			)}

			{warnings.length > 0 && (
				<section className={styles.panelSection}>
					<h4>{t('seats-preview-warnings', 'Warnings')}</h4>
					<ul>
						{warnings.map((w, i) => (
							<li key={i}>{w}</li>
						))}
					</ul>
				</section>
			)}
		</details>
	);
};
